/**
 * TALEPLER (Demands) types — BusDemand / VisaDemand / GroupTicketDemand — migration guide
 * Bölüm 3.3 (BusDemandController) & 3.9 (GroupTicketDemand/VisaDemand).
 *
 * All three DTOs' field names below are CONFIRMED against the real API in two ways:
 *  1. The live swagger document (GET /swagger/docs/v1 on the API host) exposes full
 *     `BusDemand`/`VisaDemand`/`GroupTicketDemand` schemas (PascalCase; camelCased here per
 *     the client's response normalization — see api/client.ts).
 *  2. Curl round-trips against the real test API: `GetVisaDemands` returned 9 real rows
 *     (confirms every VisaDemand field + live example values), `AddBusDemand`/
 *     `AddGroupTicketDemand`/`EditBusDemand`/`GetGroupTicketDemandById` all round-tripped
 *     successfully with the shapes below.
 *
 * Request convention (confirmed, matches corporate.ts's established pattern): body is
 * `{ <entityKeyCamelCase>: <Entity> }`; apiPost's `withSession` adds `sessionHeader` at the
 * top level alongside it automatically — do not add sessionHeader manually when calling apiPost.
 *
 * Response convention (confirmed via curl for all three, incl. the *ById routes): always
 * `{ <entityKeyCamelCase>Demands: Entity[] }` — plural array, even for GetById/Add/Edit
 * which return a single-element array (GetVisaDemandById on a real id: `{"VisaDemands":[{...}]}`).
 *
 * KNOWN SERVER QUIRK (confirmed via curl): `GroupTicketDemand/EditGroupTicketDemand` throws
 * a server-side NullReferenceException ("Object reference not set to an instance of an
 * object.") when `airlinePreference` is omitted/null — even though `AddGroupTicketDemand`
 * tolerates it being absent. Always send `airlinePreference: []` (or the real list) on edit.
 */
import type { ResponseBase, SessionHeader } from './common';

/* ------------------------------- Bus Demand ------------------------------- */

/** Otobüs bileti talebi. Confirmed via curl (AddBusDemand/EditBusDemand/GetBusDemandById
 *  round-trips) and the live swagger schema. */
export interface BusDemand {
  /** Server-assigned GUID string; absent on create. */
  id?: string;
  passengerName?: string;
  passengerSurname?: string;
  passengerNationality?: string;
  passengerIdNumber?: string;
  passengerMail?: string;
  passengerPhone?: string;
  userId?: number;
  from?: string;
  to?: string;
  /** ISO date string, e.g. "2026-09-01". */
  depDate?: string;
  /** ISO date string; only meaningful for round-trip requests. */
  retDate?: string;
  /** Read-only, server-set. */
  insertedAt?: string;
  /** Back-office status flag — set via EditBusDemand once the ticket has been issued. */
  isTicketed?: boolean;
  /** TODO(api-unconfirmed): swagger types this as a free-text string (not boolean), purpose
   *  unclear beyond "one-way related" naming — probably a fare/route class code, not a flag.
   *  Wired as an optional free-text field pending a real example with a non-boolean-looking value. */
  ow?: string;
  /** TODO(api-unconfirmed): see `ow` above — the round-trip counterpart. */
  rt?: string;
  deleted?: boolean;
  /** Read-only, derived from the session's GSA. */
  gsaId?: number;
  /** Corporate employee this demand is for — required (empty AddBusDemand 500s with a
   *  NullReferenceException; passing employeeID fixes it, and the server auto-fills
   *  passengerMail/passengerPhone/part of passengerName FROM the employee record). */
  employeeID?: number;
  /** Preferred departure time window, e.g. "08:00" (free text — no confirmed format). */
  depTime?: string;
  depTime2?: string;
  retTime?: string;
  retTime2?: string;
}

export interface BusDemandRequest {
  busDemand: Partial<BusDemand>;
}

export interface BusDemandListResponse extends ResponseBase {
  /** Confirmed via curl: field is named `busDemands` (plural) even for GetById/Add/Edit,
   *  which return a single-element array. */
  busDemands: BusDemand[];
}

/* ------------------------------ Visa Demand ------------------------------ */

/** Confirmed via curl against GetVisaDemands (9 real rows) — every field below has been
 *  observed with real, non-null example values in at least one row. */
export interface VisaDemand {
  id?: string;
  passengerName?: string;
  passengerSurname?: string;
  passengerMail?: string;
  passengerPhone?: string;
  userId?: number;
  passportNo?: string;
  /** Free text in live data, e.g. "Bordo". */
  passportType?: string;
  /** ISO date string. */
  passportIssueDate?: string;
  passportExpiryDate?: string;
  /** Free text in live data; observed values: "İŞ", "EĞİTİM", "TEDAVİ", "DİĞER" — see
   *  VISA_TRAVEL_PURPOSES below. */
  travelPurpose?: string;
  /** ISO-2 country code, e.g. "TR". */
  nationality?: string;
  /** Sic — typed as string (not number) in the real schema, even though it holds a count
   *  of days as text, e.g. "12". */
  dayCount?: string;
  /** ISO-2 destination country code, e.g. "IT", "FR", "NL". */
  country?: string;
  depDate?: string;
  retDate?: string;
  insertedAt?: string;
  /** TODO(api-unconfirmed): numeric status enum, values 0/1/2 observed live (7 rows at 0,
   *  2 rows at 2; a fresh AddVisaDemand defaulted to 1). No enum labels exist in swagger.
   *  Best-guess mapping below (0=Beklemede matches the majority of real historical rows,
   *  which reads as the natural "awaiting review" default) — see VISA_APPROVAL_STATUS_LABELS. */
  isApproved?: number;
  deleted?: boolean;
  gsaId?: number;
  employeeID?: number;
  /** Free-text reason/note attached to the demand (e.g. rejection reason or applicant note). */
  reason?: string;
}

export const VISA_TRAVEL_PURPOSES = ['İŞ', 'EĞİTİM', 'TEDAVİ', 'DİĞER'] as const;

/** TODO(api-unconfirmed): see VisaDemand.isApproved doc comment — order/labels are a
 *  best guess pending a confirmed enum source. */
export const VISA_APPROVAL_STATUS_LABELS: Record<number, string> = {
  0: 'Beklemede',
  1: 'Onaylandı',
  2: 'Reddedildi',
};

export interface VisaDemandRequest {
  visaDemand: Partial<VisaDemand>;
}

export interface VisaDemandListResponse extends ResponseBase {
  visaDemands: VisaDemand[];
}

/* --------------------------- Group Ticket Demand --------------------------- */

/** Grup bilet talebi. Confirmed via curl (AddGroupTicketDemand/GetGroupTicketDemandById
 *  round-trips) and the live swagger schema. */
export interface GroupTicketDemand {
  id?: string;
  userId?: number;
  from?: string;
  to?: string;
  /** MUST be sent as an array (possibly empty) on EditGroupTicketDemand — see file header
   *  "KNOWN SERVER QUIRK". Add tolerates it being omitted/null; Edit does not. */
  airlinePreference?: string[];
  personCount?: number;
  isOtelIncluded?: boolean;
  isVisaIncluded?: boolean;
  isTransferIncluded?: boolean;
  /** TODO(api-unconfirmed): see BusDemand.ow doc comment — same free-text-string ambiguity. */
  ow?: string;
  rt?: string;
  depDate?: string;
  retDate?: string;
  insertedAt?: string;
  /** Unlike VisaDemand, confirmed as a plain boolean here (not a 3-state numeric enum). */
  isApproved?: boolean;
  deleted?: boolean;
  gsaId?: number;
}

export interface GroupTicketDemandRequest {
  groupTicketDemand: Partial<GroupTicketDemand>;
}

export interface GroupTicketDemandListResponse extends ResponseBase {
  groupTicketDemands: GroupTicketDemand[];
}

/** Bare-session request shape for the three `Get*Demands` list routes — confirmed via curl
 *  (`BusDemand/GetBusDemands` etc. all accept `{sessionId, sessionToken}` with no wrapper). */
export type GetDemandsRequest = SessionHeader;
